/**
 * 출구 배관 배압 한계 판정.
 *
 * 근거
 *  - API 520 Part I 10판(2020)
 *    · 5.3.1.2~4  중첩배압(superimposed) / 축적배압(built-up) / 총 배압
 *    · 5.3.3.1.3 식 (1)  일반형의 허용 축적배압
 *                        PB,allowable = AOP = MAWP × (1 + %AA/100) − Pset
 *    · 5.3.3.2.1  벨로우즈는 **총 배압**이 설정압의 약 50 % 를 넘지 않는 범위에서
 *                 적용할 수 있다. 정확한 한계는 제작사 확인.
 *    · 5.3.3.3    파일럿형은 리프트가 배압의 영향을 받지 않는다.
 *                 압축성 유체 임계유동에서는 Kb = 1.0.
 *  - API 520 Part II 6판(2015) 6.3
 *    · 출구 배관은 정격용량 기준으로 사이징하는 것이 일반적.
 *    · 열팽창 전용 밸브는 축적배압 한계를 맞추지 않아도 되는 경우가 많다(6.3.2).
 *
 * 단위: 압력 kPa (게이지)
 */

export type BackpressureValveType =
  | "conventional"
  | "balanced-bellows"
  | "pilot-operated";

/** 벨로우즈의 통상 적용 한계 — 총 배압 / 설정압 (5.3.3.2.1). */
export const BELLOWS_TOTAL_BACKPRESSURE_FRACTION = 0.5;

export interface BackpressureInput {
  /** Pset — 설정압 (kPag) */
  readonly setPressureKpaGauge: number;
  /** MAWP — 설비 최고허용사용압력 (kPag) */
  readonly mawpKpaGauge: number;
  /** %AA — 허용 축적 (%). 운전 단일 10 / 복수 16 / 화재 21 */
  readonly allowableAccumulationPercent: number;
  /** 중첩배압 (kPag) — 밸브가 열리기 전부터 걸려 있는 압력 */
  readonly superimposedKpaGauge: number;
  /** 축적배압 (kPag) — 배출 중 배관에서 생기는 압력 */
  readonly builtUpKpaGauge: number;
  readonly valveType: BackpressureValveType;
  /**
   * 열팽창 전용 밸브 여부 (Part II 6.3.2).
   * 정격용량이 소요유량보다 한 자릿수 이상 커서 배관이 정상상태 유동에
   * 도달하지 않으므로, 축적배압 한계를 맞추지 않아도 되는 경우가 많다.
   */
  readonly thermalReliefOnly?: boolean;
}

export interface BackpressureResult {
  readonly ok: true;
  /** 총 배압 = 중첩 + 축적 (kPag) */
  readonly totalKpaGauge: number;
  /** 총 배압 / 설정압 (%) */
  readonly totalPercentOfSet: number;
  /** 축적배압 / 설정압 (%) */
  readonly builtUpPercentOfSet: number;
  /**
   * 허용 축적배압 (kPa). 일반형만 식 (1) 로 정해진다.
   * 벨로우즈·파일럿은 축적배압 자체의 한계가 아니라 다른 기준을 쓰므로 null.
   */
  readonly allowableBuiltUpKpa: number | null;
  /** 축적배압 판정 통과 여부 */
  readonly builtUpWithinLimit: boolean;
  /** 총 배압 판정 통과 여부 (벨로우즈의 50 % 기준 등) */
  readonly totalWithinLimit: boolean;
  readonly criterion: string;
  readonly warnings: readonly string[];
}

export interface BackpressureFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

/**
 * 일반형의 허용 축적배압 — API 520 Part I 식 (1).
 *
 *   PB,allowable = AOP = MAWP × (1 + %AA/100) − Pset
 *
 * **설정압이 아니라 MAWP 기준**인 점이 중요하다. 설정압을 MAWP 보다 낮게
 * 잡으면 허용 축적배압이 그만큼 커진다. 설정압의 10 % 로 외우면 설정압이
 * MAWP 와 같을 때만 맞는다.
 */
export function allowableBuiltUpBackpressure(
  mawpKpaGauge: number,
  allowableAccumulationPercent: number,
  setPressureKpaGauge: number,
): number {
  return (
    mawpKpaGauge * (1 + allowableAccumulationPercent / 100) - setPressureKpaGauge
  );
}

export function checkBackpressure(
  input: BackpressureInput,
): BackpressureResult | BackpressureFailure {
  const {
    setPressureKpaGauge: Pset,
    mawpKpaGauge: MAWP,
    allowableAccumulationPercent: AA,
    superimposedKpaGauge: Psi,
    builtUpKpaGauge: Pbu,
    valveType,
  } = input;

  if (!Number.isFinite(Pset) || Pset <= 0) {
    return { ok: false, reason: "invalid-input", message: "설정압은 0보다 커야 합니다." };
  }
  if (!Number.isFinite(MAWP) || MAWP <= 0) {
    return { ok: false, reason: "invalid-input", message: "MAWP 는 0보다 커야 합니다." };
  }
  if (!Number.isFinite(AA) || AA <= 0) {
    return { ok: false, reason: "invalid-input", message: "허용 축적은 0보다 커야 합니다." };
  }
  for (const [label, v] of [
    ["중첩배압", Psi],
    ["축적배압", Pbu],
  ] as const) {
    if (!Number.isFinite(v) || v < 0) {
      return { ok: false, reason: "invalid-input", message: `${label}은 0 이상이어야 합니다.` };
    }
  }

  const total = Psi + Pbu;
  const warnings: string[] = [];

  if (Pset > MAWP) {
    warnings.push(
      "설정압이 MAWP 를 넘습니다. 단일 설치에서는 설정압이 MAWP 이하여야 합니다" +
        "(API 520 Part I 5.4.2.1.1).",
    );
  }

  let allowableBuiltUpKpa: number | null = null;
  let builtUpWithinLimit = true;
  let totalWithinLimit = true;
  let criterion: string;

  switch (valveType) {
    case "conventional": {
      allowableBuiltUpKpa = allowableBuiltUpBackpressure(MAWP, AA, Pset);
      builtUpWithinLimit = Pbu <= allowableBuiltUpKpa;
      criterion = "축적배압 ≤ MAWP × (1 + %AA) − Pset  (Part I 식 1)";
      if (Psi > 0) {
        warnings.push(
          "일반형은 중첩배압이 밸브를 닫는 방향으로 작용합니다. 중첩배압이 " +
            "일정하면 스프링 설정(CDTP)으로 보정하고, 크게 변동하면 벨로우즈나 " +
            "파일럿형을 검토하십시오(5.3.2).",
        );
      }
      break;
    }
    case "balanced-bellows": {
      const limit = Pset * BELLOWS_TOTAL_BACKPRESSURE_FRACTION;
      totalWithinLimit = total <= limit;
      criterion = `총 배압 ≤ 설정압 × ${BELLOWS_TOTAL_BACKPRESSURE_FRACTION * 100} %  (Part I 5.3.3.2.1)`;
      warnings.push(
        "50 % 는 통상 적용 범위입니다. 실제 한계와 배압 보정계수 Kb 는 " +
          "제작사 자료로 확인해야 합니다(5.3.3.2.1).",
      );
      break;
    }
    case "pilot-operated": {
      criterion = "리프트가 배압의 영향을 받지 않음 (Part I 5.3.3.3)";
      warnings.push(
        "파일럿형은 리프트가 배압에 영향받지 않아 축적배압 한계가 따로 없습니다. " +
          "다만 배압이 용량에는 영향을 줄 수 있고, 압축성 유체 임계유동에서는 " +
          "Kb = 1.0 을 씁니다(5.3.3.3). 파일럿 배기 경로의 배압도 확인하십시오.",
      );
      break;
    }
  }

  if (input.thermalReliefOnly === true) {
    builtUpWithinLimit = true;
    totalWithinLimit = true;
    warnings.push(
      "열팽창 전용 밸브로 보아 축적배압 한계를 적용하지 않았습니다" +
        "(Part II 6.3.2). 공정열로 압력이 생길 수 있는 설비이거나, 긴 배관·대형 " +
        "액체 저장설비처럼 소요유량이 정격용량에 근접하는 경우에는 이 예외를 쓸 수 없습니다.",
    );
  }

  return {
    ok: true,
    totalKpaGauge: total,
    totalPercentOfSet: (total / Pset) * 100,
    builtUpPercentOfSet: (Pbu / Pset) * 100,
    allowableBuiltUpKpa,
    builtUpWithinLimit,
    totalWithinLimit,
    criterion,
    warnings,
  };
}
